import React, {Component, FormEvent, FocusEvent, ReactNode} from "react";
import MaterialTextField from "@material-ui/core/TextField";
import './index.scss';
import {ValidatedFieldProps, ValidatedFieldState} from 'components/forms/validation';

export interface TextFieldProps extends ValidatedFieldProps {
  disabled?: boolean;
  name?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  type?: 'email'|'tel'|'text'|'url'|'password'|'number';
  onInput?: (ev:FormEvent<HTMLInputElement>)=>void;
  onBlur?: (ev:FocusEvent<HTMLInputElement>)=>void;
  className?: string;
  variant?: 'outlined'|'standard',
  startAdornment?: ReactNode,
  endAdornment?: ReactNode,
  inputRef?: (el:HTMLInputElement|null)=>void;
  maxLength?: number
}

export default class TextField extends Component<TextFieldProps, ValidatedFieldState> {
  state: ValidatedFieldState = {
    dirty: false,
    touched: false,
    validationMessage: undefined
  };

  componentDidMount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.addFieldListener(this.props.name, this.onValidChanged);
    }
  }

  componentWillUnmount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.removeFieldListener(this.props.name, this.onValidChanged);
    }
  }

  onValidChanged = (errors: string[], forceDirty: boolean) => {
    const touched = this.state.touched || forceDirty;
    const dirty = this.state.dirty || forceDirty;
    if(errors) {
      let msg = this.props.validationMessages ? this.props.validationMessages[errors[0]] : null;
      if(!msg) {
        msg = errors[0];
      }
      this.setState({ validationMessage: msg, dirty: dirty, touched: touched });
    } else {
      this.setState({ validationMessage: undefined, dirty: dirty, touched: touched });
    }
  };

  onInput = (ev: FormEvent<HTMLInputElement>) => {
    const value = (ev.target as HTMLInputElement).value;
    this.setState({dirty: true}, () => {
      if(this.props.model && this.props.name) {
        this.props.model[this.props.name] = value;
      }
      if(this.props.validationGroup && this.props.name) {
        this.props.validationGroup.validateField(this.props.name);
      }
    });
    if(this.props.onInput) {
      this.props.onInput(ev);
    }
  };

  onBlur = (ev: FocusEvent<HTMLInputElement>) => {
    this.setState({touched: true});
    if(this.props.onBlur) {
      this.props.onBlur(ev);
    }
  };

  onRendered = (el: HTMLElement|null) => {
    if(!this.props.inputRef) {
      return;
    }
    const input = el?.querySelector('input') || null;
    this.props.inputRef(input);
  };

  render() {
    const showError = this.state.touched && this.state.dirty && !!this.state.validationMessage;
    const fullClassName = this.props.className ? `text-field ${this.props.className}` : 'text-field';
    const fieldProps = {
      onInput: this.onInput,
      onBlur: this.onBlur,
      disabled: this.props.disabled,
      required: this.props.required,
      label: this.props.label,
      type: this.props.type,
      error: showError,
      className: fullClassName,
      helperText: showError ? this.state.validationMessage : undefined,
      multiline: this.props.multiline,
      rows: this.props.multiline ? 6 : 1,
      placeholder: this.props.placeholder,
      value: undefined
    };
    if(this.props.model && this.props.name) {
      fieldProps.value = this.props.model[this.props.name];
    }
    const inputProps = {
      maxLength: this.props.maxLength
    };
    const inputComponentProps = {
      startAdornment: this.props.startAdornment,
      endAdornment: this.props.endAdornment
    };
    if(this.props.variant === 'standard') {
      return <MaterialTextField variant="standard" inputProps={inputProps} InputProps={inputComponentProps} ref={this.onRendered} {...fieldProps} />
    } else {
      return <MaterialTextField variant="outlined" inputProps={inputProps} InputProps={inputComponentProps} ref={this.onRendered} {...fieldProps} />
    }
  }
}